import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, CourtGridData, GridSelection, GridBookingLine, seatLimit } from '../api';
import Icon from '../design/Icon';
import { Sheet, inr, splitTime, fmtTime, fmtDateShort, fmtDateLong, todayStr, addDaysStr } from '../design/atoms';
import { MemberGym } from '../hooks/useMemberGyms';
import { BookingPay, canBookUnpaid, goToPay, gymCollectsBookings, paidChargeLine, paidCta } from '../bookingPay';
import FrontDeskNote from '../components/FrontDeskNote';

/* Court grid — court × time cells for grid-mode bookables (SPEC 03 family). */

interface OutletCtx {
  activeGymId: string | null;
  activeGym: MemberGym | null;
  loading: boolean;
}

const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
const toHHMM = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

interface CourtLine {
  roomId: string;
  name: string;
  startTime: string;
  endTime: string;
  cells: number;
}

export default function MemberCourtGridPage() {
  const { token } = useMemberAuth();
  const { classId } = useParams<{ classId: string }>();
  const { activeGymId, activeGym, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // The grid endpoint doesn't return the bookable's name — carried via nav state.
  const serviceName = (location.state as { name?: string } | null)?.name ?? null;

  const today = todayStr();
  const dates = useMemo(() => Array.from({ length: 14 }, (_, i) => addDaysStr(today, i)), [today]);

  const [date, setDate] = useState(searchParams.get('date') ?? today);
  const [grid, setGrid] = useState<CourtGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // courtId → ascending, contiguous selected cell indices
  const [sel, setSel] = useState<Record<string, number[]>>({});
  const [warn, setWarn] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<{ lines: GridBookingLine[]; paid: boolean } | null>(null);

  const branchId = activeGym?.branches[0]?.id;

  const load = useCallback(() => {
    if (!token || !activeGymId || !classId) return;
    setLoading(true);
    setError(null);
    setSel({});
    setWarn(null);
    mfetch(token, '/member/court-grid', {
      params: { gymId: activeGymId, classId, date, branchId },
    })
      .then(r => setGrid(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load the court grid'))
      .finally(() => setLoading(false));
  }, [token, activeGymId, classId, date, branchId]);

  useEffect(load, [load]);

  // Time rows mirror the backend's cell loop: dayStart..dayEnd stepping cellMinutes
  const rowTimes = useMemo(() => {
    if (!grid) return [];
    const out: string[] = [];
    for (let t = toMin(grid.dayStart); t + grid.cellMinutes <= toMin(grid.dayEnd); t += grid.cellMinutes) {
      out.push(toHHMM(t));
    }
    return out;
  }, [grid]);

  const toggle = (courtId: string, idx: number, available: boolean) => {
    if (!available || !grid) return;
    const cur = sel[courtId] ?? [];
    const next = { ...sel };
    let warning: string | null = null;

    if (cur.includes(idx)) {
      if (idx === cur[0] || idx === cur[cur.length - 1]) {
        const trimmed = cur.filter(i => i !== idx);
        if (trimmed.length === 0) delete next[courtId];
        else next[courtId] = trimmed;
      } else {
        // Removing a middle cell would split the run — restart from the tap
        next[courtId] = [idx];
      }
    } else {
      const contiguous = cur.length === 0 || idx === cur[0] - 1 || idx === cur[cur.length - 1] + 1;
      if (!contiguous) {
        next[courtId] = [idx];
      } else if (cur.length === 0 && Object.keys(sel).length >= seatLimit(grid, 'courts')) {
        const limit = seatLimit(grid, 'courts');
        warning = `You can book up to ${limit} court${limit === 1 ? '' : 's'} at once.`;
      } else if (grid.maxDurationCells != null && cur.length + 1 > grid.maxDurationCells) {
        warning = `Maximum ${grid.maxDurationCells * grid.cellMinutes} min per court.`;
      } else {
        next[courtId] = [...cur, idx].sort((a, b) => a - b);
      }
    }

    setWarn(warning);
    if (!warning) setSel(next);
  };

  const totalCells = Object.values(sel).reduce((n, arr) => n + arr.length, 0);
  const courtCount = Object.keys(sel).length;
  const belowMin = grid ? Object.values(sel).some(arr => arr.length < grid.minDurationCells) : false;

  const lines: CourtLine[] = grid
    ? Object.entries(sel).map(([roomId, arr]) => ({
        roomId,
        name: grid.courts.find(c => c.id === roomId)?.name ?? 'Court',
        startTime: rowTimes[arr[0]],
        endTime: toHHMM(toMin(rowTimes[arr[arr.length - 1]]) + grid.cellMinutes),
        cells: arr.length,
      }))
    : [];

  const totalLabel = grid
    ? grid.entitlement === 'paid' && grid.pricePerCell != null
      ? inr(grid.pricePerCell * totalCells)
      : grid.entitlement === 'pack'
        ? `${totalCells} pack credit${totalCells === 1 ? '' : 's'}`
        : 'Included'
    : '';

  if (!gymsLoading && !activeGymId) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Book</div>
        <div className="card flat"><div className="sub wrap">Join a gym to start booking sessions.</div></div>
      </div>
    );
  }

  if (done && grid) {
    const totalUnits = done.lines.reduce((n, b) => n + b.units, 0);
    const charged =
      done.paid && grid.pricePerCell != null ? paidChargeLine(grid.pricePerCell * totalUnits, activeGym) :
      grid.entitlement === 'pack' ? `${totalUnits} pack credit${totalUnits === 1 ? '' : 's'}` :
      'Included in plan';
    return (
      <div className="ok-wrap">
        <div className="ok">
          <i /><i />
          <b>
            <svg width={38} height={38} viewBox="0 0 24 24" fill="none" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          </b>
        </div>
        <div className="col rise" style={{ alignItems: 'center', gap: 6, textAlign: 'center' }}>
          <div className="lbl-d">You're booked</div>
          <div className="dp dp-xl" style={{ whiteSpace: 'normal' }}>{serviceName ?? 'Court booking'}</div>
          <div className="sub">{fmtDateLong(date)}</div>
        </div>
        <div className="card rise-2" style={{ width: '100%', maxWidth: 340, marginTop: 22 }}>
          {done.lines.map(b => (
            <div key={b.id} className="lrow">
              <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
                <div className="mid trunc">{b.roomName}</div>
                <div className="tiny">{fmtTime(b.startTime)} – {fmtTime(b.endTime)}</div>
              </div>
              <span className="tag">{b.units} slot{b.units === 1 ? '' : 's'}</span>
            </div>
          ))}
          <div className="lrow">
            <span className="lbl" style={{ width: 74 }}>{done.paid ? 'Payment' : 'Charged'}</span>
            <span className="mid trunc" style={{ flex: 1, textAlign: 'right' }}>{charged}</span>
          </div>
        </div>
        <div className="col rise-2" style={{ width: '100%', maxWidth: 340, gap: 10, marginTop: 18 }}>
          <button className="btn gold block" onClick={() => navigate('/app/bookings')}>View my bookings</button>
          <button className="btn ghost block" onClick={() => { setDone(null); load(); }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mpage">
      {/* Header */}
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate('/app/book')}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="dp dp-l trunc" style={{ minWidth: 0 }}>{serviceName ?? 'Book a court'}</div>
      </div>

      {/* Date strip */}
      <div className="hscroll">
        {dates.map(d => {
          const on = d === date;
          const dt = new Date(`${d}T00:00:00`);
          return (
            <button
              key={d}
              onClick={() => setDate(d)}
              className="col"
              style={{
                alignItems: 'center', gap: 3, padding: '8px 0', minWidth: 46, flex: '0 0 46px',
                borderRadius: 14,
                background: on ? 'var(--ink)' : 'var(--card)',
                border: `1px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                color: on ? 'var(--on-ink)' : 'var(--ink)',
              }}
            >
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.1em', opacity: .6 }}>
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][dt.getDay()]}
              </div>
              <div className="num" style={{ fontSize: 17 }}>{dt.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="col" style={{ gap: 9 }}>
          {[44, 220, 44].map((h, i) => <div key={i} className="skel" style={{ height: h }} />)}
        </div>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : !grid || grid.courts.length === 0 ? (
        <div className="card flat" style={{ textAlign: 'center', padding: 26 }}>
          <div className="mid">No courts at this branch yet</div>
          <div className="tiny wrap" style={{ marginTop: 4 }}>Ask the front desk about court bookings.</div>
        </div>
      ) : (
        <>
          <div className="between" style={{ padding: '0 2px' }}>
            <div className="lbl">Pick your slots</div>
            <div className="tiny">{grid.cellMinutes} min each</div>
          </div>

          <div style={{ overflowX: 'auto', margin: '0 -20px', padding: '2px 20px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `48px repeat(${grid.courts.length}, minmax(64px, 1fr))`,
                gap: 6,
                minWidth: 48 + grid.courts.length * 70,
              }}
            >
              <div />
              {grid.courts.map(c => (
                <div key={c.id} className="lbl trunc" style={{ textAlign: 'center', padding: '2px 0' }}>{c.name}</div>
              ))}
              {rowTimes.map((t, i) => {
                const { time, ampm } = splitTime(t);
                return (
                  <Fragment key={t}>
                    <div className="col" style={{ alignItems: 'flex-end', justifyContent: 'center', paddingRight: 4, gap: 0 }}>
                      <span className="num" style={{ fontSize: 12 }}>{time}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.08em', color: 'var(--ink-3)' }}>{ampm}</span>
                    </div>
                    {grid.courts.map(c => {
                      const available = c.cells[i]?.available ?? false;
                      const on = (sel[c.id] ?? []).includes(i);
                      return (
                        <button
                          key={c.id}
                          disabled={!available}
                          aria-label={`${c.name} ${fmtTime(t)}`}
                          aria-pressed={on}
                          onClick={() => toggle(c.id, i, available)}
                          style={{
                            minHeight: 44,
                            borderRadius: 'var(--r-s)',
                            border: `1px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                            background: on ? 'var(--ink)' : available ? 'var(--card)' : 'var(--bg-2)',
                            opacity: available || on ? 1 : .55,
                            boxShadow: available && !on ? 'var(--sh-s)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background .16s, transform .16s var(--ease)',
                          }}
                        >
                          {on && <Icon n="check" s={15} c="var(--on-ink)" />}
                        </button>
                      );
                    })}
                  </Fragment>
                );
              })}
            </div>
          </div>

          {warn && <div className="tiny wrap" style={{ color: 'var(--clay)', padding: '0 2px' }}>{warn}</div>}

          {/* Keep the grid tail visible above the fixed summary bar */}
          {totalCells > 0 && <div style={{ height: 84, flex: '0 0 84px' }} />}
        </>
      )}

      {/* Sticky summary bar (above the tab bar) */}
      {grid && totalCells > 0 && (
        <div
          style={{
            position: 'fixed', bottom: 'var(--tabbar-clearance)', left: '50%', transform: 'translateX(-50%)',
            width: '100%', maxWidth: 480, padding: '0 20px', zIndex: 30,
          }}
        >
          <div className="card" style={{ padding: '12px 14px', boxShadow: 'var(--sh-l)' }}>
            {belowMin && (
              <div className="tiny wrap" style={{ color: 'var(--clay)', marginBottom: 8 }}>
                Minimum booking is {grid.minDurationCells * grid.cellMinutes} min per court.
              </div>
            )}
            <div className="between">
              <div className="col" style={{ gap: 2, minWidth: 0 }}>
                <div className="mid">
                  {totalCells} slot{totalCells === 1 ? '' : 's'} · {courtCount} court{courtCount === 1 ? '' : 's'}
                </div>
                <div className="tiny">{totalLabel}</div>
              </div>
              <button className="btn gold sm" disabled={belowMin} onClick={() => setConfirming(true)}>Book</button>
            </div>
          </div>
        </div>
      )}

      {confirming && grid && (
        <CourtConfirmSheet
          grid={grid}
          lines={lines}
          totalLabel={totalLabel}
          gymId={activeGymId!}
          gym={activeGym}
          classId={classId!}
          date={date}
          token={token!}
          onClose={() => setConfirming(false)}
          onBooked={(bookings, paid) => {
            setConfirming(false);
            setSel({});
            setDone({ lines: bookings, paid });
          }}
        />
      )}
    </div>
  );
}

// ── Confirm sheet (court checkout) ──────────────────────────────────────────
function CourtConfirmSheet({
  grid, lines, totalLabel, gymId, gym, classId, date, token, onClose, onBooked,
}: {
  grid: CourtGridData;
  lines: CourtLine[];
  totalLabel: string;
  gymId: string;
  gym: MemberGym | null;
  classId: string;
  date: string;
  token: string;
  onClose: () => void;
  onBooked: (bookings: GridBookingLine[], paid: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalCells = lines.reduce((n, l) => n + l.cells, 0);
  const paid = grid.entitlement === 'paid' && grid.pricePerCell != null;
  const deskOnly = grid.entitlement === 'paid' && !canBookUnpaid(grid.allowUnpaidBooking, gym);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const started = Date.now();
    try {
      const selections: GridSelection[] = lines.map(l => ({ roomId: l.roomId, startTime: l.startTime, cells: l.cells }));
      const r = await mfetch(token, '/member/bookings', {
        method: 'POST',
        body: { gymId, classId, branchId: grid.branchId, date, selections },
      });
      // Where the gym collects, every court is held while the member pays — one payment.
      const pay: BookingPay | null = r.data?.pay ?? null;
      if (pay) return goToPay(pay.url);
      // Keep a 400ms minimum so the busy state reads as deliberate (MOTION.md)
      const wait = Math.max(0, 400 - (Date.now() - started));
      setTimeout(() => onBooked(r.data?.bookings ?? [], paid), wait);
    } catch (e) {
      setBusy(false);
      if (e instanceof MemberApiError) {
        if (e.code === 'BOOKING_CONFLICT' && e.body?.conflicting) {
          setError(`You already have ${e.body.conflicting.name} at ${fmtTime(e.body.conflicting.startTime)} that day.`);
        } else {
          setError(e.message);
        }
      } else {
        setError('Could not complete the booking. Please try again.');
      }
    }
  };

  return (
    <Sheet onClose={busy ? () => {} : onClose}>
      <div className="between">
        <div className="row" style={{ gap: 6 }}>
          <span className="tag">Courts</span>
          <span className="tag">{grid.cellMinutes} min slots</span>
        </div>
        <button className="icon-btn" aria-label="Close" onClick={onClose}>
          <Icon n="x" s={16} c="var(--ink-2)" />
        </button>
      </div>

      <div className="col" style={{ gap: 4 }}>
        <div className="dp dp-m" style={{ whiteSpace: 'normal' }}>Confirm booking</div>
        <div className="sub">{fmtDateShort(date)}</div>
      </div>

      <div className="card flat" style={{ padding: '4px 12px' }}>
        {lines.map(l => (
          <div key={l.roomId} className="lrow">
            <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
              <div className="mid trunc">{l.name}</div>
              <div className="tiny">
                {fmtTime(l.startTime)}–{fmtTime(l.endTime)} · {l.cells} slot{l.cells === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="field">
        <span className="lbl">Your rate</span>
        <b>{totalLabel}</b>
      </div>

      <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
        <Icon n="shield" s={15} c="var(--ink-3)" />
        <span className="tiny wrap" style={{ lineHeight: 1.4 }}>
          Free cancellation until {grid.cancellationCutoffHours} {grid.cancellationCutoffHours === 1 ? 'hr' : 'hrs'} before the session.
          {grid.entitlement === 'pack' ? ' Late cancels use one credit per slot.' : ''}
        </span>
      </div>

      {error && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--clay)' }}>{error}</span>
        </div>
      )}

      {deskOnly ? (
        <FrontDeskNote gym={gym} />
      ) : (
        <button className="btn gold block" disabled={busy} onClick={confirm}>
          {busy ? (gymCollectsBookings(gym) && paid ? 'Opening payment…' : 'Confirming…')
            : paid ? paidCta(grid.pricePerCell! * totalCells, gym) : 'Confirm booking'}
        </button>
      )}
    </Sheet>
  );
}
